import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  FileDown,
  ImagePlus,
  Printer,
  QrCode,
  Trash2,
} from 'lucide-react';
import { JOURNEY_LIBRARY } from '../../../src/data/journeyLibrary.js';
import { visibleTrusts } from '../lib/accessApi';
import { trusts } from '../lib/trustLibrary';
import { loadCloudTrustConfig, loadPatientAccessState } from '../lib/trustConfigApi';
import { PILOT_POSTER_TEMPLATES } from '../lib/posterTemplates';
import {
  buildJourneySignpostingUrl,
  buildPosterTargetUrl,
} from '../lib/signpostingLinks';
import {
  downloadCanvasPdf,
  downloadCanvasPng,
  downloadQrPng,
  readImageFile,
  renderPoster,
  safeFilename,
} from '../lib/posterExport';

const patientAppBaseUrl = (import.meta.env.VITE_PATIENT_APP_URL || 'https://knowyourcare.co.uk').replace(/\/$/, '');

function copyText(value, setNotice) {
  navigator.clipboard?.writeText(value)
    .then(() => setNotice('Link copied to clipboard.'))
    .catch(() => setNotice('Copy failed. Select and copy the link manually.'));
}

function journeyCampaign(journeyId) {
  return `studio_${String(journeyId || 'journey').replace(/[^a-z0-9_-]/gi, '_').toLowerCase()}`.slice(0, 80);
}

export default function SignpostingToolsStudio({ workspaceSwitcher = null, studioAccess = null, studioTrusts = trusts }) {
  const scopedTrusts = visibleTrusts(studioAccess, studioTrusts);
  const platformAdmin = studioAccess?.roles?.includes('platform_admin');
  const initialTrustId = scopedTrusts.find((trust) => trust.id !== 'generic_nhs')?.id || scopedTrusts[0]?.id || 'generic_nhs';

  const [trustId, setTrustId] = useState(initialTrustId);
  const [configState, setConfigState] = useState({ configuration: null, publication: {} });
  const [patientAccess, setPatientAccess] = useState(null);
  const [journeyId, setJourneyId] = useState('');
  const [selectedPosterId, setSelectedPosterId] = useState('generic');
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [logoName, setLogoName] = useState('');
  const [previewState, setPreviewState] = useState('idle');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const canvasRef = useRef(null);

  const loadTrust = async (nextTrustId) => {
    setTrustId(nextTrustId);
    setLogoDataUrl(null);
    setLogoName('');
    setPatientAccess(null);
    try {
      const response = await loadCloudTrustConfig(nextTrustId);
      setConfigState(response);
      if (platformAdmin) {
        try {
          setPatientAccess(await loadPatientAccessState(nextTrustId));
        } catch {
          setPatientAccess(null);
        }
      }
    } catch (error) {
      setConfigState({ configuration: null, publication: {} });
      setNotice(error.message || 'Signposting configuration could not be loaded.');
    }
  };

  useEffect(() => {
    loadTrust(initialTrustId);
  }, [initialTrustId]);

  const config = configState.configuration;
  const selectedTrust = scopedTrusts.find((trust) => trust.id === trustId) || null;

  const configuredJourneys = useMemo(() => {
    const configured = config?.journeys || {};
    return JOURNEY_LIBRARY
      .filter((template) => configured[template.id])
      .map((template) => ({
        id: template.id,
        title: configured[template.id]?.title || template.title || template.id,
      }));
  }, [config]);

  useEffect(() => {
    if (!configuredJourneys.length) {
      setJourneyId('');
      return;
    }
    if (!configuredJourneys.some((journey) => journey.id === journeyId)) {
      setJourneyId(configuredJourneys[0].id);
    }
  }, [configuredJourneys, journeyId]);

  const selectedJourney = configuredJourneys.find((journey) => journey.id === journeyId) || configuredJourneys[0] || null;
  const journeyUrl = selectedJourney
    ? buildJourneySignpostingUrl(patientAppBaseUrl, trustId, selectedJourney.id, journeyCampaign(selectedJourney.id))
    : '';

  const selectedPoster = PILOT_POSTER_TEMPLATES.find((template) => template.id === selectedPosterId) || PILOT_POSTER_TEMPLATES[0];
  const posterJourneyConfigured = selectedPoster.targetType === 'home'
    || configuredJourneys.some((journey) => journey.id === selectedPoster.journeyId);
  const posterTargetUrl = posterJourneyConfigured
    ? buildPosterTargetUrl(patientAppBaseUrl, trustId, selectedPoster)
    : '';
  const liveRelease = Boolean(configState.publication?.live || configState.publication?.current);
  const canExportPoster = Boolean(posterJourneyConfigured && posterTargetUrl && logoDataUrl);
  const trustLabel = selectedTrust?.shortName || selectedTrust?.name || trustId;
  const posterFileBase = `KYC_${safeFilename(trustLabel)}_${safeFilename(selectedPoster.shortLabel)}_A4`;

  useEffect(() => {
    let cancelled = false;
    if (!canvasRef.current || !posterJourneyConfigured || !posterTargetUrl) {
      setPreviewState('idle');
      return undefined;
    }
    setPreviewState('rendering');
    renderPoster(canvasRef.current, {
      template: selectedPoster,
      targetUrl: posterTargetUrl,
      logoDataUrl,
    }).then(() => {
      if (!cancelled) setPreviewState('ready');
    }).catch((error) => {
      if (!cancelled) {
        setPreviewState('error');
        setNotice(error.message || 'Poster preview could not be rendered.');
      }
    });
    return () => { cancelled = true; };
  }, [selectedPoster, posterJourneyConfigured, posterTargetUrl, logoDataUrl]);

  const choosePoster = (template) => {
    const available = template.targetType === 'home'
      || configuredJourneys.some((journey) => journey.id === template.journeyId);
    if (!available) {
      setNotice(`${template.label} needs its Journey to be configured for ${trustLabel} first.`);
      return;
    }
    setSelectedPosterId(template.id);
    if (template.journeyId) setJourneyId(template.journeyId);
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    try {
      const dataUrl = await readImageFile(file);
      setLogoDataUrl(dataUrl);
      setLogoName(file.name);
      setNotice('Trust logo added to the poster preview for this session.');
    } catch (error) {
      setLogoDataUrl(null);
      setLogoName('');
      setNotice(error.message || 'Trust logo could not be added.');
    } finally {
      event.target.value = '';
    }
  };

  const ensurePosterRendered = async () => {
    if (!canvasRef.current || !posterJourneyConfigured || !posterTargetUrl) {
      throw new Error('Choose an available poster before exporting.');
    }
    return renderPoster(canvasRef.current, {
      template: selectedPoster,
      targetUrl: posterTargetUrl,
      logoDataUrl,
    });
  };

  const downloadPoster = async (format) => {
    if (!canExportPoster || busy) return;
    setBusy(true);
    try {
      const canvas = await ensurePosterRendered();
      if (format === 'pdf') {
        await downloadCanvasPdf(canvas, `${posterFileBase}.pdf`);
      } else {
        await downloadCanvasPng(canvas, `${posterFileBase}.png`);
      }
      setNotice(`${selectedPoster.label} downloaded with the Trust logo and first-party QR code.`);
    } catch (error) {
      setNotice(error.message || 'Poster export failed.');
    } finally {
      setBusy(false);
    }
  };

  const downloadQr = async () => {
    if (!posterTargetUrl || busy) return;
    setBusy(true);
    try {
      await downloadQrPng(
        posterTargetUrl,
        `KYC_${safeFilename(trustLabel)}_${safeFilename(selectedPoster.shortLabel)}_QR.png`,
      );
      setNotice('QR code downloaded as a high-resolution PNG.');
    } catch (error) {
      setNotice(error.message || 'QR export failed.');
    } finally {
      setBusy(false);
    }
  };

  return <div className="app-shell">
    <header className="topbar">
      <div className="brand">
        <span className="brand-mark"><QrCode/></span>
        <div><strong>Know Your Care<sup className="registered-mark">®</sup> Studio</strong><small>Signposting tools</small></div>
      </div>
      <div className="topbar-actions">
        {scopedTrusts.length > 1 && <label className="context-select">
          <select value={trustId} onChange={(event) => loadTrust(event.target.value)}>
            {scopedTrusts.map((trust) => <option key={trust.id} value={trust.id}>{trust.shortName}</option>)}
          </select>
        </label>}
      </div>
    </header>

    {notice && <div className="studio-notice" role="status">
      <span>{notice}</span>
      <button onClick={() => setNotice('')} aria-label="Dismiss notification">×</button>
    </div>}

    <div className="workspace">
      <aside className="catalogue">
        {workspaceSwitcher}
        <div className="catalogue-intro">
          <p className="eyebrow">Patient signposting</p>
          <h1>Signposting tools</h1>
          <p>Create Trust-aware links, QR codes and approved print-ready posters without using an external QR service.</p>
        </div>
      </aside>

      <main className="review-workspace">
        <section className="trust-config-panel">
          <div className="trust-panel-heading">
            <div>
              <p className="eyebrow">Journey link generator</p>
              <h2>Choose a configured Journey</h2>
              <p>Links include the selected Trust so a new patient device opens in the correct Trust context.</p>
            </div>
          </div>
          <div className="config-fields" style={{paddingTop:'1rem'}}>
            <label>Journey
              <select value={selectedJourney?.id || ''} onChange={(event) => setJourneyId(event.target.value)} disabled={!configuredJourneys.length}>
                {configuredJourneys.length
                  ? configuredJourneys.map((journey) => <option key={journey.id} value={journey.id}>{journey.title}</option>)
                  : <option value="">No configured Journeys yet</option>}
              </select>
            </label>
            <label>Trust-aware patient link<input readOnly value={journeyUrl}/></label>
          </div>
          <div className="trust-actions">
            <button className="button secondary" type="button" onClick={() => copyText(journeyUrl, setNotice)} disabled={!journeyUrl}><Copy size={16}/>Copy link</button>
            <a className="button secondary" href={journeyUrl || undefined} target="_blank" rel="noreferrer" aria-disabled={!journeyUrl}><ExternalLink size={16}/>Open patient link</a>
          </div>
        </section>

        <section className="trust-config-panel" style={{marginTop:'1rem'}}>
          <div className="trust-panel-heading">
            <div>
              <p className="eyebrow">Approved poster templates</p>
              <h2>Choose a poster</h2>
              <p>The artwork and wording are locked. Studio adds only the Trust logo and the correct first-party QR code.</p>
            </div>
          </div>
          <div className="signposting-preset-grid">
            {PILOT_POSTER_TEMPLATES.map((template) => {
              const available = template.targetType === 'home'
                || configuredJourneys.some((journey) => journey.id === template.journeyId);
              const active = selectedPoster.id === template.id;
              return <article key={template.id} className={`${active ? 'active' : ''} ${available ? '' : 'unavailable'}`}>
                <span>{available ? <CheckCircle2 size={24}/> : <AlertTriangle size={24}/>}</span>
                <h3>{template.label}</h3>
                <strong>{template.targetType === 'home' ? 'Opens the Trust-aware KYC home page' : `Opens the ${template.qrLabel}`}</strong>
                <p>{available
                  ? (template.targetType === 'home' ? 'Always available for this Trust.' : 'The required Journey is configured for this Trust.')
                  : 'Configure this Journey before generating its poster.'}</p>
                <button className={active ? 'button primary' : 'button secondary'} type="button" onClick={() => choosePoster(template)} disabled={!available}>
                  <Printer size={16}/>{active ? 'Selected' : 'Use this poster'}
                </button>
              </article>;
            })}
          </div>
        </section>

        <section className="trust-config-panel" style={{marginTop:'1rem'}}>
          <div className="trust-panel-heading">
            <div>
              <p className="eyebrow">Poster generator</p>
              <h2>{selectedPoster.label}</h2>
              <p>Generate the final A4 poster locally in your browser. The uploaded Trust logo is used only for this Studio session.</p>
            </div>
            <div className={liveRelease ? 'poster-readiness ready' : 'poster-readiness warning'}>
              {liveRelease ? <CheckCircle2 size={17}/> : <AlertTriangle size={17}/>}
              <span>{liveRelease ? 'Published Trust release available' : 'Not yet published'}</span>
            </div>
          </div>

          <div className="poster-generator-grid">
            <div className="poster-generator-controls">
              <div className="poster-control-card">
                <span className="poster-step">1</span>
                <div>
                  <strong>Confirm the QR destination</strong>
                  <p>{selectedPoster.qrLabel}</p>
                  <input className="poster-link-input" readOnly value={posterTargetUrl}/>
                  <div className="poster-inline-actions">
                    <button className="button secondary" type="button" onClick={() => copyText(posterTargetUrl, setNotice)} disabled={!posterTargetUrl}><Copy size={15}/>Copy</button>
                    <a className="button secondary" href={posterTargetUrl || undefined} target="_blank" rel="noreferrer" aria-disabled={!posterTargetUrl}><ExternalLink size={15}/>Test link</a>
                    <button className="button secondary" type="button" onClick={downloadQr} disabled={!posterTargetUrl || busy}><QrCode size={15}/>Download QR</button>
                  </div>
                </div>
              </div>

              <div className="poster-control-card">
                <span className="poster-step">2</span>
                <div>
                  <strong>Add the approved Trust logo</strong>
                  <p>PNG, JPG, WebP or SVG. Studio fits it automatically into the reserved logo area.</p>
                  <label className="button secondary poster-upload-button">
                    <ImagePlus size={16}/>{logoDataUrl ? 'Replace Trust logo' : 'Upload Trust logo'}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={uploadLogo}/>
                  </label>
                  {logoDataUrl && <div className="poster-logo-status">
                    <CheckCircle2 size={16}/><span>{logoName || 'Trust logo added'}</span>
                    <button type="button" onClick={() => { setLogoDataUrl(null); setLogoName(''); }} aria-label="Remove Trust logo"><Trash2 size={15}/></button>
                  </div>}
                </div>
              </div>

              <div className="poster-control-card">
                <span className="poster-step">3</span>
                <div>
                  <strong>Download the final print artwork</strong>
                  <p>The QR code and Trust logo are composited onto the approved master at its full 1449 × 2048 resolution.</p>
                  {!logoDataUrl && <small className="poster-export-warning">Add the Trust logo to enable final poster download.</small>}
                  {platformAdmin && patientAccess && <small className={patientAccess.patientAccessEnabled ? 'poster-access-state ready' : 'poster-access-state warning'}>
                    Patient Access: {patientAccess.patientAccessEnabled ? 'enabled' : 'disabled'}
                  </small>}
                  {!platformAdmin && <small className="poster-access-state">Patient Access is enabled separately by Platform Admin before distribution.</small>}
                  <div className="poster-inline-actions">
                    <button className="button primary" type="button" onClick={() => downloadPoster('pdf')} disabled={!canExportPoster || busy}><FileDown size={16}/>{busy ? 'Preparing…' : 'Download A4 PDF'}</button>
                    <button className="button secondary" type="button" onClick={() => downloadPoster('png')} disabled={!canExportPoster || busy}><Download size={16}/>Download PNG</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="poster-preview-panel">
              <div className="poster-preview-heading">
                <div><strong>Live poster preview</strong><span>{trustLabel}</span></div>
                <small>{previewState === 'rendering' ? 'Updating…' : previewState === 'ready' ? 'Ready' : 'Waiting for poster target'}</small>
              </div>
              {posterJourneyConfigured
                ? <canvas ref={canvasRef} className="poster-preview-canvas" aria-label={`${selectedPoster.label} preview`}/>
                : <div className="signposting-placeholder"><AlertTriangle size={34}/><div><strong>Journey configuration required</strong><span>Configure the {selectedPoster.qrLabel} before generating this poster.</span></div></div>}
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>;
}
